/**
 * Tâche 1274 (Eric 2026-06-29) — Diagnostic orthographe dans une vraie fenêtre.
 *
 * Le bouton ouvrait un `window.alert` listant des clés techniques
 * (`corrections_pending: 3`). En parallèle, trois bandeaux horizontaux au-dessus
 * de la transcription portaient les mêmes chiffres, chacun sur sa ligne.
 * Ici, les trois bandeaux ET la boîte système sont réunis ; les bandeaux
 * restent sur la page mais se replient d'un clic sur le triangle.
 */

import { createPortal } from "react-dom";

const fmtMin = (s) => (s === null || s === undefined ? "?" : `${Math.round((s / 60) * 10) / 10} min`);

export default function SpellingDiagnosticModal({ open, onClose, merged, coverage, lang = "fr" }) {
  if (!open) return null;

  const m = merged || {};
  const segs = m.segments || [];
  const d = m.fusion_diagnostic || {};

  let totalCorrections = 0, accepted = 0, rejected = 0, pending = 0, editedSegments = 0;
  const dupKeys = new Map();
  for (const s of segs) {
    const cs = s.corrections || [];
    totalCorrections += cs.length;
    for (const c of cs) {
      if (c.accepted) accepted += 1;
      else if (c.rejected) rejected += 1;
      else pending += 1;
      const k = `${c.original || ""}||${c.correction || ""}`;
      dupKeys.set(k, (dupKeys.get(k) || 0) + 1);
    }
    if (s.is_modified) editedSegments += 1;
  }
  const duplicatedPairs = Array.from(dupKeys.values()).filter((n) => n > 1).length;

  const minors = segs.flatMap((s) => (s.corrections || []).filter((c) => c.category === "minor"));
  const acceptedMinors = minors.filter((c) => c.accepted && !c.rejected).length;

  const srcSec = d.source_covered_duration_sec ?? coverage?.srcEnd ?? null;
  const mergedSec = d.merged_covered_duration_sec ?? coverage?.mergedEnd ?? null;
  const ratio = d.merged_coverage_ratio ?? coverage?.ratio ?? null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="spelling-diagnostic-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-zinc-200 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
            <span aria-hidden="true">📝</span>
            {lang === "fr" ? "Diagnostic orthographe" : "Spelling diagnostic"}
          </h2>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 text-xl leading-none"
            data-testid="spelling-diagnostic-close-btn"
            aria-label="close"
          >×</button>
        </div>

        <div className="p-4 space-y-4">
          {/* 1er bandeau — corrections appliquées + légende des couleurs. */}
          <section data-testid="spelling-diagnostic-applied">
            <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">
              {lang === "fr" ? "Corrections appliquées" : "Applied corrections"}
            </h3>
            <p className="text-[12px] text-zinc-800">
              <strong data-testid="spelling-diagnostic-corrections-count">{m.corrections_count || 0}</strong>{" "}
              {lang === "fr" ? "corrections appliquées ·" : "corrections applied ·"}{" "}
              <strong>{m.modified_segments_count || 0}</strong>{" "}
              {lang === "fr" ? "segments modifiés" : "segments modified"}
            </p>
            <p className="mt-1 text-[11px] text-zinc-600">
              <span className="text-zinc-700">{lang === "fr" ? "texte normal" : "plain text"}</span> = {lang === "fr" ? "commun" : "common"} ·{" "}
              <span className="bg-rose-100 text-rose-900 font-semibold px-1 rounded">{lang === "fr" ? "mot" : "word"}</span> = {lang === "fr" ? "proposé" : "proposed"} ·{" "}
              <span className="bg-blue-100 text-blue-800 font-semibold px-1 rounded">{lang === "fr" ? "mot" : "word"}</span> = {lang === "fr" ? "accepté" : "accepted"}
            </p>
          </section>

          {/* 2e bandeau — couverture de la fusion. */}
          <section data-testid="spelling-diagnostic-coverage">
            <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">
              {lang === "fr" ? "Couverture de la fusion" : "Merge coverage"}
            </h3>
            <p className="text-[12px] text-zinc-800">
              {srcSec != null && mergedSec != null ? (
                <>
                  <strong>{fmtMin(mergedSec)}</strong> / <strong>{fmtMin(srcSec)}</strong>{" "}
                  {lang === "fr" ? "couvertes" : "covered"}
                  {ratio != null && <> · <span data-testid="spelling-diagnostic-coverage-pct">{Math.round(ratio * 100)} %</span></>}
                </>
              ) : (
                lang === "fr" ? "Couverture inconnue (aucun horodatage exploitable)" : "Unknown coverage (no usable timecodes)"
              )}
            </p>
            {d.fireflies_only_injected_count > 0 && (
              <p className="mt-1 text-[11px] text-violet-800">
                {lang === "fr"
                  ? `+ ${d.fireflies_only_injected_sec} s récupérées de Fireflies (${d.fireflies_only_injected_count} passage(s))`
                  : `+ ${d.fireflies_only_injected_sec}s recovered from Fireflies (${d.fireflies_only_injected_count})`}
              </p>
            )}
            {d.skipped_empty_source_segments > 0 && (
              <p className="mt-1 text-[11px] text-zinc-600">
                {lang === "fr"
                  ? `${d.skipped_empty_source_segments} segment(s) source vide(s) ignoré(s)`
                  : `${d.skipped_empty_source_segments} empty source segment(s) skipped`}
              </p>
            )}
          </section>

          {/* 3e bandeau — corrections mineures. */}
          <section data-testid="spelling-diagnostic-minors">
            <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">
              {lang === "fr" ? "Corrections mineures (orthographe / accents / ponctuation)" : "Minor corrections"}
            </h3>
            <p className="text-[12px] text-zinc-800" data-testid="spelling-diagnostic-minors-count">
              <strong>{acceptedMinors}</strong> / {minors.length} {lang === "fr" ? "acceptée(s)" : "accepted"}
            </p>
            <p className="mt-1 text-[11px] text-zinc-500 italic">
              {lang === "fr"
                ? "Les boutons « Tout accepter » et « Tout retirer » restent sur le bandeau, au-dessus de la transcription."
                : "The « Accept all » / « Unaccept all » buttons stay on the banner above the transcript."}
            </p>
          </section>

          {/* Ce que disait la boîte système. */}
          <section data-testid="spelling-diagnostic-internals">
            <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">
              {lang === "fr" ? "Détail par segment" : "Per-segment detail"}
            </h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
              <dt className="text-zinc-500">{lang === "fr" ? "Segments" : "Segments"}</dt>
              <dd className="text-zinc-900 tabular-nums">{segs.length}</dd>
              <dt className="text-zinc-500">{lang === "fr" ? "Segments retouchés" : "Edited segments"}</dt>
              <dd className="text-zinc-900 tabular-nums">{editedSegments}</dd>
              <dt className="text-zinc-500">{lang === "fr" ? "Corrections au total" : "Total corrections"}</dt>
              <dd className="text-zinc-900 tabular-nums">{totalCorrections}</dd>
              <dt className="text-zinc-500">{lang === "fr" ? "Acceptées" : "Accepted"}</dt>
              <dd className="text-zinc-900 tabular-nums">{accepted}</dd>
              <dt className="text-zinc-500">{lang === "fr" ? "Refusées" : "Rejected"}</dt>
              <dd className="text-zinc-900 tabular-nums">{rejected}</dd>
              <dt className="text-zinc-500">{lang === "fr" ? "En attente" : "Pending"}</dt>
              <dd className="text-zinc-900 tabular-nums" data-testid="spelling-diagnostic-pending">{pending}</dd>
              <dt className="text-zinc-500">{lang === "fr" ? "Couples en doublon" : "Duplicated pairs"}</dt>
              <dd className="text-zinc-900 tabular-nums">{duplicatedPairs}</dd>
            </dl>
          </section>
        </div>

        <div className="p-3 border-t border-zinc-200 bg-zinc-50 flex justify-end">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-100"
            data-testid="spelling-diagnostic-footer-close-btn"
          >
            {lang === "fr" ? "Fermer" : "Close"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
